/** Partner console — Edit Commercial (dedicated route).
 *
 * `/partner/commercials/:id/edit` — edits an existing commercial using the
 * SAME shared CommercialForm as the Add page (previously edit was a
 * cramped inline row on the list). Pre-filled from the commercials
 * breakdown; adds the status (active / suspended) control. */
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import {
  CommercialForm,
  type CommercialFormValues,
} from "@/components/CommercialForm";
import { PartnerPageHeader } from "@/components/PartnerLayout";
import { Spinner } from "@/components/Spinner";
import {
  usePartnerCommercials,
  usePartnerMe,
  useUpdateCommercialTerms,
} from "@/lib/partner";

export default function PartnerEditCommercialPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const me = usePartnerMe();
  const commercials = usePartnerCommercials(true);
  const update = useUpdateCommercialTerms();

  if (me.isLoading || commercials.isLoading)
    return <Spinner fullscreen label={t("common.loading")} />;
  if (me.data && me.data.kind !== "reseller")
    return <Navigate to="/partner" replace />;

  const row = (commercials.data?.commercials ?? []).find((c) => c.id === id);
  if (!row) return <Navigate to="/partner/commercials" replace />;

  const submit = (v: CommercialFormValues) => {
    update.mutate(
      {
        id: row.id,
        name: v.name,
        email: v.email,
        phone: v.phone,
        is_company: v.is_company,
        legal_name: v.legal_name,
        registration_number: v.registration_number,
        vat_id: v.vat_id,
        address_line: v.address_line,
        city: v.city,
        postal_code: v.postal_code,
        address_country: v.address_country,
        commission_model: v.commission_model,
        rate_base_pct: v.rate_base_pct,
        rate_session_pct: v.rate_session_pct,
        rate_signature_pct: v.rate_signature_pct,
        fixed_base_eur: v.fixed_base_eur,
        fixed_session_eur: v.fixed_session_eur,
        fixed_signature_eur: v.fixed_signature_eur,
        commission_duration: v.commission_duration,
        status: v.status,
      },
      { onSuccess: () => navigate("/partner/commercials") },
    );
  };

  return (
    <>
      <PartnerPageHeader
        eyebrow={t("nav.partner.commercials")}
        title={t("partner.commercials.editTitle", "Edit commercial")}
        description={row.name}
      />
      <CommercialForm
        mode="edit"
        showStatus
        initial={{
          name: row.name,
          email: row.email,
          phone: row.phone ?? "",
          is_company: !!row.is_company,
          legal_name: row.legal_name ?? "",
          registration_number: row.registration_number ?? "",
          vat_id: row.vat_id ?? "",
          address_line: row.address_line ?? "",
          city: row.city ?? "",
          postal_code: row.postal_code ?? "",
          address_country: row.address_country ?? "",
          commission_model: row.commission_model,
          rate_base_pct: String(row.rate_base_pct),
          rate_session_pct: String(row.rate_session_pct),
          rate_signature_pct: String(row.rate_signature_pct),
          fixed_base_eur: String(row.fixed_base_eur ?? 0),
          fixed_session_eur: String(row.fixed_session_eur ?? 0),
          fixed_signature_eur: String(row.fixed_signature_eur ?? 0),
          commission_duration: row.commission_duration,
          status: row.status,
        }}
        submitting={update.isPending}
        error={update.isError}
        submitLabel={
          update.isPending ? t("common.saving", "Saving…") : t("common.save")
        }
        onSubmit={submit}
        onCancel={() => navigate("/partner/commercials")}
      />
    </>
  );
}
